import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, DollarSign, Shield, Users } from 'lucide-react';

const InvestmentSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#1F4E78]">
          {t(
            'Strategic Acceleration Capital',
            'Capital de Aceleración Estratégica'
          )}
        </h2>
        <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t(
            '€330K Seed Round | €50M Pre-Money | Exit Q3 2028',
            '€330K Ronda Seed | €50M Pre-Money | Salida Q3 2028'
          )}
        </p>

        {/* Use of Funds */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-[#1F4E78]">
              {t('Use of Funds Breakdown', 'Desglose de Uso de Fondos')}
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-800">
                    {t('R&D', 'I+D')}
                  </span>
                  <span className="text-2xl font-bold text-[#2E5090]">80%</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">€264K</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• {t('Lead candidate synthesis & characterization', 'Síntesis y caracterización de candidato principal')}</li>
                  <li>• {t('In vitro validation studies', 'Estudios de validación in vitro')}</li>
                  <li>• {t('In vivo efficacy studies', 'Estudios de eficacia in vivo')}</li>
                </ul>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-800">
                    {t('IP/Regulatory', 'PI/Regulatorio')}
                  </span>
                  <span className="text-2xl font-bold text-[#2E5090]">15%</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">€49.5K</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• {t('PCT international filing', 'Presentación internacional PCT')}</li>
                  <li>• {t('Divisional patent applications', 'Solicitudes de patentes divisionales')}</li>
                </ul>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-800">
                    {t('Operations', 'Operaciones')}
                  </span>
                  <span className="text-2xl font-bold text-[#2E5090]">5%</span>
                </div>
                <p className="text-sm text-gray-600">€16.5K</p>
              </div>
            </div>
          </div>

          {/* Investment Terms */}
          <div className="bg-gradient-to-br from-[#1F4E78] to-[#2E5090] text-white rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-6">{t('Investment Terms', 'Términos de Inversión')}</h3>
            <div className="space-y-6">
              <div>
                <div className="text-sm opacity-80 mb-1">{t('Pre-money Valuation', 'Valoración Pre-money')}</div>
                <div className="text-3xl font-bold">€50M</div>
              </div>
              <div>
                <div className="text-sm opacity-80 mb-1">{t('Investment Amount', 'Monto de Inversión')}</div>
                <div className="text-3xl font-bold">€330K</div>
              </div>
              <div>
                <div className="text-sm opacity-80 mb-1">{t('Post-money Valuation', 'Valoración Post-money')}</div>
                <div className="text-3xl font-bold">€50.33M</div>
              </div>
              <div>
                <div className="text-sm opacity-80 mb-1">{t('Dilution', 'Dilución')}</div>
                <div className="text-3xl font-bold">0.66%</div>
              </div>
              <div>
                <div className="text-sm opacity-80 mb-1">{t('Founders Retain', 'Fundadores Retienen')}</div>
                <div className="text-3xl font-bold">99.34%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Capital Efficiency */}
        <div className="bg-[#FFD700]/10 border-2 border-[#FFD700] rounded-xl p-8 mb-12">
          <h3 className="text-2xl font-bold mb-6 text-center text-[#1F4E78]">
            {t('Capital Efficiency Extreme', 'Eficiencia de Capital Extrema')}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2E5090] mb-2">2.5x</div>
              <div className="text-sm text-gray-700">
                {t(
                  'MORE R&D than typical biotech seed with SAME CAPITAL',
                  'MÁS I+D que biotech seed típica con MISMO CAPITAL'
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2E5090] mb-2">€0</div>
              <div className="text-sm text-gray-700">
                {t(
                  'Founder salary (financial independence enables 100% to R&D)',
                  'Salario del fundador (independencia financiera permite 100% a I+D)'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ROI Projections */}
        <div className="bg-gray-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold mb-6 text-center text-[#1F4E78]">
            {t('ROI Projections', 'Proyecciones de ROI')}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">{t('Conservative', 'Conservador')}</div>
              <div className="text-3xl font-bold text-[#2E5090] mb-2">228x</div>
              <div className="text-sm text-gray-600">MOIC</div>
              <div className="text-2xl font-bold text-gray-800 mt-3">237%</div>
              <div className="text-sm text-gray-600">TIR</div>
            </div>
            <div className="bg-gradient-to-br from-[#1F4E78] to-[#2E5090] text-white rounded-lg p-6 text-center transform scale-105 shadow-lg">
              <div className="text-sm opacity-80 mb-2">{t('Base Case', 'Caso Base')}</div>
              <div className="text-3xl font-bold mb-2">440x</div>
              <div className="text-sm opacity-80">MOIC</div>
              <div className="text-2xl font-bold mt-3">289%</div>
              <div className="text-sm opacity-80">TIR</div>
            </div>
            <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">{t('Best Case', 'Mejor Caso')}</div>
              <div className="text-3xl font-bold text-[#2E5090] mb-2">647x</div>
              <div className="text-sm text-gray-600">MOIC</div>
              <div className="text-2xl font-bold text-gray-800 mt-3">340%</div>
              <div className="text-sm text-gray-600">TIR</div>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center mt-6">
            {t(
              'Based on licensing deal range €10M-€35M upfront + 8% royalties | 30-month timeline to Q3 2028',
              'Basado en rango de acuerdo de licenciamiento €10M-€35M inicial + 8% regalías | Cronograma de 30 meses hasta Q3 2028'
            )}
          </p>
        </div>

        {/* Target Investors */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold mb-4 text-[#1F4E78]">
            {t('Target Investors', 'Inversores Objetivo')}
          </h3>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {t(
              'Seeking 22-33 strategic angels with pharma networks and biotech experience, or 1-2 micro-VCs. Minimum investment €10K. Looking for smart capital that brings strategic value beyond funding.',
              'Buscando 22-33 ángeles estratégicos con redes farmacéuticas y experiencia en biotech, o 1-2 micro-VCs. Inversión mínima €10K. Buscando capital inteligente que aporte valor estratégico más allá de financiamiento.'
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

export default InvestmentSection;
